import { UserAvatar } from "components/Atoms/Avatar";
import { PhosphorChatTeardropDots, PhosphorCheck, PhosphorEye, PhosphorStack, PhosphorX } from "components/Atoms/PhosphorIcon";
import moment from "moment";
import React from "react";
import { formatWrittenDateTimeUtc, newLineToBr } from "utilities";

interface Props {
  activity: server.dto.AdvancingSessionActivity
}

// Row content is formatted as "{label}" or "{label} : {value}"
const rowContentSeparator = " : ";

const splitRowContent = (content: string) => {
  const separatorIndex = content.indexOf(rowContentSeparator);
  if (separatorIndex === -1) return { label: content, value: undefined };

  return { label: content.slice(0, separatorIndex), value: content.slice(separatorIndex + rowContentSeparator.length) };
};

// The server has no action text for the row actions yet
const getTitle = (activity: server.dto.AdvancingSessionActivity) => {
  switch (activity.action.value) {
    case "participantLinkedAccount": return RESX.AdvancingTimeline.acceptedTheInvitation;
    case "providedChange": return `provided ${splitRowContent(activity.content).label}`;
    case "approvedRow": return `accepted ${splitRowContent(activity.content).label}`;
    case "declinedRow": return `declined ${splitRowContent(activity.content).label}`;
    default: return activity.action.text;
  }
};

const getContent = (activity: server.dto.AdvancingSessionActivity) => {
  switch (activity.action.value) {
    case "providedChange":
    case "approvedRow":
    case "declinedRow":
      return splitRowContent(activity.content).value;
    default: return activity.content;
  }
};

const ActivityIcon = ({ action }: { action: server.dto.AdvancingSessionActions }) => {
  switch (action) {
    case "providedChange":
      return <div className="advancing-activity-icon purple"><PhosphorStack iconSize="small" iconColor="white" /></div>;
    case "approvedRow":
      return <div className="advancing-activity-icon green"><PhosphorCheck iconSize="small" iconColor="white" /></div>;
    case "declinedRow":
      return <div className="advancing-activity-icon pink"><PhosphorX iconSize="small" iconColor="white" /></div>;
    case "sentMessageFromAgent":
    case "sentMessageToAgent":
      return <div className="advancing-activity-icon blue"><PhosphorChatTeardropDots iconSize="small" iconColor="white" /></div>;
    default:
      return <div className="advancing-activity-icon gray"><PhosphorEye iconSize="small" iconColor="white" /></div>;
  }
};

export const AdvancingActivityRow = ({ activity }: Props) => {
  const timestampUtc = moment.utc(activity.timestampUtc);
  const content = getContent(activity);

  return <div className="advancing-activity-row">
    <div className="advancing-activity-card card-surface-standalone">
      {activity.actor && <div>
        <UserAvatar user={activity.actor} />
      </div>}
      <div className="grow1 flex-column gap-small">
        <div className="flex justify-space-between gap-large">
          <div>{activity.actorName} {getTitle(activity)}</div>
          <div className="text-sublabel nowrap" title={formatWrittenDateTimeUtc(timestampUtc)}>{timestampUtc.fromNow()}</div>
        </div>
        {content && <div className="advancing-activity-content">{newLineToBr(content)}</div>}
      </div>
    </div>
    <div className="advancing-activity-rail">
      <ActivityIcon action={activity.action.value} />
      <div className="advancing-activity-rail-line" />
    </div>
  </div>;
};
